/* ============================================================
 * Billest "Value-Confident" — small theme behaviors
 *  1. Scroll-reveal (gated on prefers-reduced-motion)
 *  2. Style-rail arrow scrolling (scroll by one card width)
 * No dependencies. Safe to load on every page.
 * ============================================================ */
(function () {
  'use strict';

  var reduce =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- 1. Scroll reveal ---- */
  function initReveal() {
    var els = document.querySelectorAll('.vc-reveal');
    if (!els.length) return;

    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- 2. Style-rail arrows ---- */
  function initRails() {
    var rails = document.querySelectorAll('[data-vc-rail]');
    rails.forEach(function (rail) {
      var track = rail.querySelector('[data-vc-rail-track]');
      var prev = rail.querySelector('[data-vc-rail-prev]');
      var next = rail.querySelector('[data-vc-rail-next]');
      if (!track) return;

      function step() {
        var card = track.querySelector('[data-vc-card]');
        if (!card) return track.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0) || 0;
        return card.getBoundingClientRect().width + gap;
      }

      function update() {
        if (!prev || !next) return;
        var max = track.scrollWidth - track.clientWidth - 1;
        prev.disabled = track.scrollLeft <= 1;
        next.disabled = track.scrollLeft >= max;
      }

      if (prev) prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: reduce ? 'auto' : 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: reduce ? 'auto' : 'smooth' });
      });

      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---- 3. Brand film: play in view, pause off-screen ---- */
  function initFilm() {
    var stages = document.querySelectorAll('[data-vc-film]');
    stages.forEach(function (stage) {
      if (stage.__vcInit) return;
      stage.__vcInit = true;

      var video = stage.querySelector('video');
      var toggle = stage.querySelector('[data-vc-film-toggle]');
      if (!video) return;

      // iOS needs these for inline muted playback regardless of what
      // the video_tag filter emitted.
      video.muted = true;
      video.setAttribute('playsinline', '');

      // Reduced motion: never autoplay — the poster sits still until
      // the visitor presses play themselves.
      var userPaused = reduce;
      if (reduce) stage.classList.add('is-paused');

      function play() {
        var p = video.play();
        if (p && p.catch) p.catch(function () { setPaused(true); });
        stage.classList.remove('is-paused');
        if (toggle) {
          toggle.setAttribute('aria-label', 'Pause video');
          toggle.setAttribute('aria-pressed', 'false');
        }
      }
      function setPaused(silent) {
        video.pause();
        stage.classList.add('is-paused');
        if (toggle && !silent) {
          toggle.setAttribute('aria-label', 'Play video');
          toggle.setAttribute('aria-pressed', 'true');
        }
      }

      if (toggle) {
        toggle.addEventListener('click', function () {
          userPaused = !video.paused;
          if (video.paused) play();
          else setPaused();
        });
      }

      if (!('IntersectionObserver' in window)) {
        if (!userPaused) play();
        return;
      }
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (userPaused) return;
            if (entry.isIntersecting) play();
            else setPaused(true);
          });
        },
        { threshold: 0.25 }
      );
      io.observe(stage);
    });
  }

  /* ---- 4. PDP sticky add-to-cart ---- */
  function initStickyPdp() {
    var bar = document.querySelector('[data-vc-sticky-pdp]');
    if (!bar || bar.__vcInit) return;
    bar.__vcInit = true;

    // First add-to-cart-component in document order is the main
    // product form (quick-add modals mount theirs on demand).
    var main = document.querySelector('add-to-cart-component');
    if (!main || !('IntersectionObserver' in window)) return; // bar stays hidden

    var cta = bar.querySelector('[data-vc-sticky-atc]');
    if (cta) {
      cta.addEventListener('click', function () {
        var mainBtn = main.querySelector('button');
        if (mainBtn && !mainBtn.disabled) {
          mainBtn.click();
        } else {
          main.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
        }
      });
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          // Show only once the real buy button has scrolled up out of
          // view; stay hidden while it's visible or still below.
          var scrolledPast = entry.boundingClientRect.top < 0;
          bar.hidden = entry.isIntersecting || !scrolledPast;
        });
      },
      { threshold: 0 }
    );
    io.observe(main);
  }

  /* ---- 5. Header mobile drawer ---- */
  function initHeader() {
    var drawers = document.querySelectorAll('[data-vc-drawer]');
    drawers.forEach(function (drawer) {
      if (drawer.__vcInit) return;
      drawer.__vcInit = true;

      var opener = document.querySelector('[data-vc-menu-open][aria-controls="' + drawer.id + '"]');

      function open() {
        drawer.hidden = false;
        // force reflow so the transition runs
        void drawer.offsetWidth;
        drawer.classList.add('is-open');
        if (opener) opener.setAttribute('aria-expanded', 'true');
        document.documentElement.style.overflow = 'hidden';
      }
      function close() {
        drawer.classList.remove('is-open');
        if (opener) opener.setAttribute('aria-expanded', 'false');
        document.documentElement.style.overflow = '';
        var done = function () { drawer.hidden = true; drawer.removeEventListener('transitionend', done); };
        if (reduce) { done(); } else { drawer.addEventListener('transitionend', done); }
      }

      if (opener) opener.addEventListener('click', open);
      drawer.querySelectorAll('[data-vc-menu-close]').forEach(function (el) {
        el.addEventListener('click', close);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
      });
    });
  }

  function init() {
    initReveal();
    initRails();
    initFilm();
    initStickyPdp();
    initHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init in the theme editor when sections are re-rendered.
  document.addEventListener('shopify:section:load', init);
})();
